import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getUserProfile, updateUserBio, uploadUserAvatar, getModeratedTopics } from "../services/userService";
import { getUserAvatarUrl } from "../utils/urlBuilder";
import "./Profile.css";
import { getUserPosts } from "../services/postService";
import AvatarImageDialog from "../components/AvatarImageDialog";

const DEFAULT_BIO = "這位旅人還沒有分享更多的故事。";

const normalizeProfile = (raw = {}, fallbackUsername = "") => {
  const joined = raw.joined_at || raw.joinDate || raw.created_at || raw.date_joined || new Date().toISOString();
  const metrics = (raw && typeof raw.metrics === "object" && raw.metrics !== null) ? raw.metrics : {};
  const posts = Array.isArray(raw.posts)
    ? raw.posts
    : Array.isArray(raw.recent_posts)
      ? raw.recent_posts
      : [];

  return {
    id: raw.id,
    username: raw.username || fallbackUsername,
    displayName: raw.display_name || raw.nickname || raw.full_name || raw.username || fallbackUsername,
    avatar: raw.avatar
      ? getUserAvatarUrl(raw.avatar)
      : raw.avatar_url || raw.profile_pic || null,
    joinDate: joined,
    bio: raw.bio || raw.tagline || raw.title || DEFAULT_BIO,
    location: raw.location || raw.city || "東南亞",
    metrics: {
      posts: metrics.posts ?? raw.posts_count ?? posts.length ?? 0,
      likes: metrics.likes ?? raw.likes_count ?? 0,
      visitedCities: metrics.visitedCities ?? raw.visited_cities_count ?? 0,
    },
    badges: raw.badges || [],
    interests: raw.interests || raw.travel_styles || ["夜市美食", "海島度假", "屋頂酒吧"],
    posts,
    favorites: raw.favorites || [],
    footprints: raw.footprints || [],
    topics: raw.moderated_topics || [],
    recentViews: raw.recent_views || [],
    frequentCities: raw.frequent_cities || [],
    followingTopics: raw.following_topics || [],
    permissions: raw.permissions || {},
  };
};

const createFallbackProfile = (username, user) => {
  if (user) {
    return normalizeProfile(user, user.username);
  }
  return normalizeProfile({ username }, username);
};

const Profile = () => {
  const { username: routeUsername } = useParams();
  const { user, isAuthenticated, refreshUser } = useAuth();

  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bioDraft, setBioDraft] = useState("");
  const [isSavingBio, setIsSavingBio] = useState(false);
  const [activeTab, setActiveTab] = useState("posts");
  const [statusMessage, setStatusMessage] = useState("");
  const [dataEnriched, setDataEnriched] = useState(false);

  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarCropFile, setAvatarCropFile] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef(null);

  const targetUsername = routeUsername || (isAuthenticated ? user?.username : "");
  const isOwnProfile = isAuthenticated && user && (!routeUsername || routeUsername === user.username);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setPageError("");

    const fallback = createFallbackProfile(targetUsername, user);
    setDataEnriched(false);

    try {
      const result = await getUserProfile(targetUsername);
      if (result.success && result.data) {
        const normalized = normalizeProfile(result.data, targetUsername);
        setProfileData(normalized);
        setBioDraft(normalized.bio || DEFAULT_BIO);
      } else {
        setProfileData(fallback);
        setBioDraft(fallback.bio || DEFAULT_BIO);
        if (result.error) {
          setPageError(result.error);
        }
      }
    } catch (error) {
      console.error("載入個人檔案失敗:", error);
      setProfileData(fallback);
      setBioDraft(fallback.bio || DEFAULT_BIO);
      setPageError("無法載入個人檔案，顯示暫存資料");
    } finally {
      setLoading(false);
    }
  }, [targetUsername, user]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!statusMessage) return;
    const timer = setTimeout(() => setStatusMessage(""), 3200);
    return () => clearTimeout(timer);
  }, [statusMessage]);

  const handleEditBio = () => {
    setIsEditingBio(true);
  };

  const handleCancelBio = () => {
    if (profileData) {
      setBioDraft(profileData.bio || DEFAULT_BIO);
    }
    setIsEditingBio(false);
  };

  const handleSaveBio = async () => {
    if (!bioDraft.trim()) {
      setStatusMessage("請輸入一些內容再儲存。");
      return;
    }
    setIsSavingBio(true);
    try {
      const result = await updateUserBio(bioDraft.trim());
      if (result.success) {
        setProfileData((prev) => (prev ? { ...prev, bio: bioDraft.trim() } : prev));
        setStatusMessage("個人簡介已更新");
        setIsEditingBio(false);
        if (refreshUser) {
          await refreshUser();
        }
      } else {
        setStatusMessage(result.error || "無法更新個人簡介");
      }
    } catch (error) {
      console.error("更新個人簡介失敗:", error);
      setStatusMessage("更新個人簡介時發生錯誤");
    } finally {
      setIsSavingBio(false);
    }
  };

  useEffect(() => {
    const enrichProfile = async () => {
      if (!profileData || dataEnriched) return;

      const updates = {};

      if (profileData.username) {
        const topicsResult = await getModeratedTopics(profileData.username);
        if (topicsResult.success) {
          updates.topics = topicsResult.data;
        }
      }

      if (isOwnProfile) {
        try {
          const postsResult = await getUserPosts();
          if (Array.isArray(postsResult)) {
            updates.posts = postsResult;
            updates.metrics = {
              ...profileData.metrics,
              posts: postsResult.length,
            };
          }
        } catch (error) {
          console.error("載入用戶貼文失敗:", error);
        }
      }

      if (Object.keys(updates).length) {
        setProfileData((prev) => (prev ? { ...prev, ...updates } : prev));
      }

      setDataEnriched(true);
    };

    enrichProfile();
  }, [profileData, dataEnriched, isOwnProfile]);

  const openAvatarPicker = () => {
    if (!isOwnProfile || avatarUploading) return;
    setAvatarCropFile(null);
    setShowAvatarModal(false);
    avatarInputRef.current?.click();
  };

  const handleAvatarFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAvatarCropFile(file);
    setShowAvatarModal(true);
    event.target.value = "";
  };

  const closeAvatarModal = () => {
    if (avatarInputRef.current) {
      avatarInputRef.current.value = "";
    }
    setShowAvatarModal(false);
    setAvatarCropFile(null);
  };

  const handleAvatarDialogComplete = async ({ file, dataUrl }) => {
    if (!file) {
      setStatusMessage("未取得裁切後的圖像");
      return false;
    }

    setAvatarUploading(true);
    let uploadSucceeded = false;
    try {
      const result = await uploadUserAvatar(file);
      if (result.success) {
        const serverAvatar =
          result.data?.profile_pic || result.data?.avatar || result.data?.url;
        const avatarUrl = serverAvatar ? getUserAvatarUrl(serverAvatar) : dataUrl;
        setProfileData((prev) => (prev ? { ...prev, avatar: avatarUrl } : prev));
        setStatusMessage("頭像更新成功");
        if (refreshUser) {
          await refreshUser();
        }
        uploadSucceeded = true;
      } else {
        const message =
          result.status === 401
            ? "登入已過期，請重新登入後再試"
            : result.error || "頭像更新失敗";
        setStatusMessage(message);
      }
    } catch (error) {
      console.error("上傳頭像失敗:", error);
      setStatusMessage("頭像更新時發生錯誤");
    } finally {
      setAvatarUploading(false);
    }
    return uploadSucceeded;
  };

  const tabs = useMemo(() => {
    const baseTabs = [{ key: "posts", label: "📝 貼文" }];
    if (profileData?.permissions?.can_moderate || profileData?.topics?.length) {
      baseTabs.push({ key: "topics", label: " 主題管理" });
    }
    return baseTabs;
  }, [profileData]);

  const renderTabContent = () => {
    if (!profileData) return null;
    const { posts = [], topics = [] } = profileData;

    const empty = (message) => (
      <div className="profile-empty-state">
        <p>{message}</p>
        {isOwnProfile && <p className="profile-empty-action">在首頁或貼文列表開始探索吧！</p>}
      </div>
    );

    switch (activeTab) {
      case "posts":
        return posts.length ? (
          <div className="profile-card-grid">
            {posts.map((post) => (
              <article key={post.id} className="profile-card">
                <h3>{post.title || "未命名貼文"}</h3>
                <p>{post.excerpt || "尚未提供內容摘要。"}</p>
                <div className="profile-card-meta">
                  <span>{post.topic_name || "未分類"}</span>
                  {post.created_at && (
                    <span>{new Date(post.created_at).toLocaleDateString("zh-TW")}</span>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : empty("還沒有發表任何貼文");
      case "topics":
        return topics.length ? (
          <div className="profile-card-grid">
            {topics.map((topic) => (
              <article key={topic.id || topic.slug} className="profile-card">
                <h3>{topic.name || "主題"}</h3>
                <p>{topic.description || "用夜晚的故事凝聚社群。"}</p>
              </article>
            ))}
          </div>
        ) : empty("目前沒有管理的主題");
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-skeleton">
          <div className="skeleton-hero" />
          <div className="skeleton-content" />
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="profile-page">
        <div className="profile-empty-state">
          <p>找不到這位旅人。</p>
        </div>
      </div>
    );
  }

  const { metrics } = profileData;

  return (
    <div className="profile-page">
      {statusMessage && (
        <div className="profile-toast" role="status">
          {statusMessage}
        </div>
      )}

      {pageError && (
        <div className="profile-banner-warning">
          ⚠️ {pageError}
        </div>
      )}

      <input
        ref={avatarInputRef}
        type="file"
        accept="image/*"
        className="profile-avatar-input"
        onChange={handleAvatarFileChange}
      />

      <section className="profile-hero">
        <div className="profile-hero-decoration" aria-hidden="true" />
        <div className="profile-hero-content">
          <div className="profile-hero-main">
            <div className={`profile-avatar-wrapper${avatarUploading ? " is-uploading" : ""}`}>
              <button
                type="button"
                className={`profile-avatar${isOwnProfile ? " is-editable" : ""}`}
                onClick={openAvatarPicker}
                aria-label={isOwnProfile ? "更新大頭貼" : undefined}
                disabled={!isOwnProfile || avatarUploading}
                aria-busy={avatarUploading}
              >
                <img
                  src={profileData.avatar || ""}
                  alt={`${profileData.displayName} 的頭像`}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    if (!profileData.username) return;
                    const canvas = document.createElement("canvas");
                    canvas.width = 160;
                    canvas.height = 160;
                    const ctx = canvas.getContext("2d");
                    ctx.fillStyle = "#FF69B4";
                    ctx.fillRect(0, 0, 160, 160);
                    ctx.fillStyle = "#fff";
                    ctx.font = "bold 72px Inter";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";
                    ctx.fillText(profileData.username.charAt(0).toUpperCase(), 80, 88);
                    e.currentTarget.src = canvas.toDataURL();
                  }}
                />
                {isOwnProfile && !avatarUploading && (
                  <span className="profile-avatar-edit">
                    <span className="profile-avatar-edit-icon" aria-hidden="true">
                      📷
                    </span>
                    點擊更換
                  </span>
                )}
                {avatarUploading && (
                  <div className="profile-avatar-status" aria-live="polite">
                    <span className="profile-avatar-spinner" aria-hidden="true" />
                    上傳中...
                  </div>
                )}
              </button>
              {isOwnProfile && (
                <p className="profile-avatar-hint">支援 JPG、PNG，建議使用 1:1 圖片。</p>
              )}
            </div>

            <div className="profile-hero-meta">
              <div className="profile-hero-header">
                <h1 className="profile-display-name">{profileData.displayName}</h1>
                {isOwnProfile && (
                  <button type="button" className="profile-toolbar-btn" onClick={handleEditBio}>
                    ✏️ 編輯個人檔案
                  </button>
                )}
              </div>
              <div className="profile-hero-subline">
                <span className="profile-username">@{profileData.username}</span>
                {profileData.location && <span className="profile-dot">•</span>}
                {profileData.location && <span className="profile-location">📍 {profileData.location}</span>}
                <span className="profile-dot">•</span>
                <span className="profile-joined">加入於 {new Date(profileData.joinDate).toLocaleDateString("zh-TW")}</span>
              </div>
              {profileData.bio && <p className="profile-bio-intro">{profileData.bio}</p>}
              {profileData.interests?.length > 0 && (
                <div className="profile-interests">
                  {profileData.interests.map((interest) => (
                    <span key={interest} className="profile-interest-pill">
                      #{interest}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="profile-hero-metrics">
            <div className="profile-metric">
              <span className="profile-metric-value">{metrics?.posts ?? 0}</span>
              <span className="profile-metric-label">貼文</span>
            </div>
            <div className="profile-metric">
              <span className="profile-metric-value">{metrics?.likes ?? 0}</span>
              <span className="profile-metric-label">獲讚</span>
            </div>
            <div className="profile-metric">
              <span className="profile-metric-value">{metrics?.visitedCities ?? 0}</span>
              <span className="profile-metric-label">造訪城市</span>
            </div>
          </div>
        </div>
      </section>

      <div className="profile-layout">
        <main className="profile-main">
          <section className="profile-section">
            <div className="profile-section-header">
              <h2 className="profile-section-title">關於我</h2>
              {isEditingBio ? (
                <div className="profile-section-actions">
                  <button
                    type="button"
                    className="profile-toolbar-btn"
                    onClick={handleSaveBio}
                    disabled={isSavingBio}
                  >
                    {isSavingBio ? "儲存中..." : "💾 儲存"}
                  </button>
                  <button type="button" className="profile-toolbar-btn ghost" onClick={handleCancelBio}>
                    取消
                  </button>
                </div>
              ) : (
                isOwnProfile && (
                  <div className="profile-section-actions">
                    <button type="button" className="profile-toolbar-btn" onClick={handleEditBio}>
                      ✏️ 編輯
                    </button>
                  </div>
                )
              )}
            </div>
            <div className="profile-section-body">
              {isEditingBio ? (
                <div className="profile-bio-editor">
                  <textarea
                    value={bioDraft}
                    onChange={(event) => setBioDraft(event.target.value)}
                    placeholder="分享一些關於您的東南亞夜晚旅行經歷..."
                    rows={4}
                    maxLength={280}
                  />
                  <div className="profile-bio-meta">
                    <span>{bioDraft.length}/280</span>
                  </div>
                </div>
              ) : (
                <p className="profile-bio-text">{profileData.bio || DEFAULT_BIO}</p>
              )}
            </div>
          </section>

          <section className="profile-section">
            <div className="profile-section-header">
              <h2 className="profile-section-title">動態</h2>
            </div>
            <div className="profile-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`profile-tab-button${tab.key === activeTab ? " is-active" : ""}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="profile-tab-content">{renderTabContent()}</div>
          </section>
        </main>

      </div>

      <AvatarImageDialog
        isOpen={showAvatarModal && !!avatarCropFile}
        initialFile={avatarCropFile}
        onClose={closeAvatarModal}
        onComplete={handleAvatarDialogComplete}
      />
    </div>
  );
};

export default Profile;
